import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Users, X, Plus, Search } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export const ContactLinker = ({ module, entityId, compact = false }) => {
  const [links, setLinks] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (module && entityId) fetchLinks();
  }, [module, entityId]);

  const fetchLinks = async () => {
    try {
      const { data } = await axios.get(
        `${API}/api/crm-links/${module}/${entityId}`,
        
      );
      setLinks(data.links || []);
    } catch {
      /* ignore */
    }
  };

  const searchContacts = async (q) => {
    if (!q || q.length < 2) {
      setResults([]);
      return;
    }
    try {
      const { data } = await axios.get(
        `${API}/api/crm-links/search-contacts?q=${encodeURIComponent(q)}`,
        
      );
      setResults(data.contacts || []);
    } catch {
      setResults([]);
    }
  };

  const addLink = async (contact) => {
    setLoading(true);
    try {
      await axios.post(
        `${API}/api/crm-links`,
        {
          contact_id: contact.id,
          module,
          entity_id: entityId,
          entity_label: contact.email || contact.name,
        },
        
      );
      fetchLinks();
      setShowSearch(false);
      setQuery('');
      setResults([]);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  };

  const removeLink = async (contactId) => {
    try {
      await axios.delete(
        `${API}/api/crm-links/${module}/${entityId}/${contactId}`,
        
      );
      fetchLinks();
    } catch {
      /* ignore */
    }
  };

  if (compact) {
    return (
      <div
        className="flex items-center gap-1.5 flex-wrap"
        data-testid={`crm-linker-${module}`}
      >
        <Users className="h-3 w-3 text-gray-400" />
        {links.map((l, i) => (
          <Badge key={i} variant="secondary" className="text-[10px] gap-1">
            {l.contact?.name || l.contact_id}
            <X
              className="h-2.5 w-2.5 cursor-pointer"
              onClick={() => removeLink(l.contact_id)}
            />
          </Badge>
        ))}
        <button
          className="text-[10px] text-blue-500 hover:text-blue-700"
          onClick={() => setShowSearch(!showSearch)}
          data-testid="add-crm-link-compact-btn"
        >
          <Plus className="h-3 w-3 inline" /> Link
        </button>
        {showSearch && (
          <div className="w-full mt-1 relative">
            <input
              type="text"
              placeholder="Search contacts..."
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                searchContacts(e.target.value);
              }}
              className="w-full text-xs border border-gray-200 rounded px-2 py-1 outline-none focus:border-blue-400"
              data-testid="crm-link-search-compact"
            />
            {results.length > 0 && (
              <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded mt-0.5 shadow-lg z-20 max-h-32 overflow-y-auto">
                {results.map((c, i) => (
                  <button
                    key={i}
                    className="w-full text-left px-2 py-1.5 text-[11px] hover:bg-gray-50"
                    onClick={() => addLink(c)}
                    disabled={loading}
                  >
                    {c.name} - {c.email}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className="border border-gray-200 rounded-lg p-3"
      data-testid={`crm-linker-${module}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-gray-500" />
          <span className="text-xs font-medium text-gray-700">Linked Contacts</span>
          <Badge variant="outline" className="text-[10px]">
            {links.length}
          </Badge>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowSearch(!showSearch)}
          data-testid="add-crm-link-btn"
          className="h-6 w-6 p-0"
        >
          <Plus className="h-3.5 w-3.5" />
        </Button>
      </div>

      {showSearch && (
        <div className="mb-2 relative">
          <div className="flex items-center gap-2 border border-gray-200 rounded-md px-2 py-1.5">
            <Search className="h-3.5 w-3.5 text-gray-400" />
            <input
              type="text"
              placeholder="Search contacts..."
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                searchContacts(e.target.value);
              }}
              className="flex-1 text-xs bg-transparent outline-none"
              data-testid="crm-link-search"
            />
          </div>
          {results.length > 0 && (
            <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-md mt-1 shadow-lg z-10 max-h-40 overflow-y-auto">
              {results.map((c, i) => (
                <button
                  key={i}
                  className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50 flex items-center justify-between"
                  onClick={() => addLink(c)}
                  disabled={loading}
                >
                  <span>
                    {c.name} {c.email && `- ${c.email}`}
                  </span>
                  <Plus className="h-3 w-3 text-gray-400" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="space-y-1">
        {links.length === 0 && (
          <p className="text-[10px] text-gray-400 py-1">No linked contacts</p>
        )}
        {links.map((l, i) => (
          <div
            key={i}
            className="flex items-center justify-between py-1 px-2 rounded bg-gray-50"
          >
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center">
                <span className="text-[9px] font-medium text-blue-600">
                  {(l.contact?.name || '?')[0].toUpperCase()}
                </span>
              </div>
              <div>
                <p className="text-[11px] font-medium">
                  {l.contact?.name || l.contact_id}
                </p>
                {l.contact?.email && (
                  <p className="text-[9px] text-gray-400">{l.contact.email}</p>
                )}
              </div>
            </div>
            <button
              onClick={() => removeLink(l.contact_id)}
              className="text-gray-400 hover:text-red-500"
              data-testid={`remove-crm-link-${i}`}
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};
